import { FC, useEffect, useMemo } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import { IModalComponentProps } from '@context/modal_context';
import { ETwoFAMethod } from '@xeppt/xeppt-sdk/types';
import VerificationCode from '@components/verification_code';
import Input from '@components/common/input';
import { useLocales } from '@hooks/helpers/useLocales';

const DisableMFA: FC<
    IModalComponentProps<{
        type: Exclude<ETwoFAMethod, ETwoFAMethod.NONE>;
        mfaCode: string;
        isCodeSent?: boolean;
        isRecoveryCode?: boolean;
    }>
> = ({ modalData, changeModalData, handleChangeDisable, modalState, changeModalState }) => {
    const { submitLocale } = useLocales();
    const renderDescription = useMemo(() => {
        if (!modalData?.isCodeSent) {
            return 'Are you sure you want to disable two-factor authentication? This will remove an extra layer of security protecting your account.';
        }
        switch (modalData?.type) {
            case ETwoFAMethod.TOTP:
                return 'To disable the authenticator app 2FA method, enter the verification code for XEPPT from your app';
            case ETwoFAMethod.SMS:
                return 'To disable the 2FA method, enter the verification code that was sent to your primary phone number';
            case ETwoFAMethod.EMAIL:
                return 'To disable the 2FA method, enter the verification code that was sent to your primary email';
        }
    }, [modalData]);

    useEffect(() => {
        if (modalData?.isCodeSent) {
            changeModalState({ ...modalState, confirmLabel: 'disable' });
        } else {
            changeModalState({ ...modalState, confirmLabel: 'yes_disable_mfa' });
        }
    }, [modalData]);

    return (
        <div className={styles.wrapper}>
            <Typography className={styles.title} variant="h4" weight="bold" alignment="center">
                {modalData?.isCodeSent ? 'Disable Two-factor authentication' : 'Verify yourself'}
            </Typography>
            <div className={styles.content}>
                <Typography variant="body3" alignment="center">
                    {renderDescription}
                </Typography>
            </div>
            {modalData?.isCodeSent && (
                <div className={styles.form}>
                    {modalData?.isRecoveryCode ? (
                        <Input
                            className={styles.input}
                            onChange={(val) => {
                                if (val.length === 0) {
                                    handleChangeDisable(true);
                                } else {
                                    handleChangeDisable(false);
                                }
                                changeModalData({ ...modalData, mfaCode: val });
                            }}
                            value={modalData?.mfaCode}
                        />
                    ) : (
                        <VerificationCode
                            onChange={(val) => {
                                if (val.length !== 6) {
                                    handleChangeDisable(true);
                                } else {
                                    handleChangeDisable(false);
                                }
                                changeModalData({ ...modalData, mfaCode: val });
                            }}
                            value={modalData?.mfaCode}
                        />
                    )}
                    {modalData?.type === ETwoFAMethod.TOTP && !modalData?.isRecoveryCode && (
                        <Typography alignment="center" variant="body4" className={styles.details}>
                            Can’t find your device?{' '}
                            <button
                                onClick={() =>
                                    changeModalData({
                                        ...modalData,
                                        isRecoveryCode: true,
                                        mfaCode: ''
                                    })
                                }>
                                {submitLocale('use_backup_code')}
                            </button>
                        </Typography>
                    )}
                </div>
            )}
        </div>
    );
};

export default DisableMFA;
